import React, { useMemo, useState } from 'react';
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, ExternalLink, X, Layers
} from 'lucide-react';
import {
  SECTION_TYPES,
  TEMPLATE_CONTENT_FIELDS,
  resolvePageBuilder,
  createBlankPage,
  createSection,
  slugifyPath,
} from '../../lib/pageDefaults';
import { adminUpdateSettings, apiErrorMessage, fetchSettings } from '../../api/services';
import ImageUploadField from './ImageUploadField';

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <input {...props} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 text-sm" />
    </div>
  );
}

function TextArea({ label, rows = 3, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <textarea rows={rows} {...props} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 text-sm resize-none" />
    </div>
  );
}

function Modal({ open, onClose, title, children, size = 'xl' }) {
  if (!open) return null;
  const sizes = { md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl', full: 'max-w-5xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${sizes[size]} max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-lg text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function SectionDataFields({ section, onChange }) {
  const d = section.data || {};
  const set = (key, value) => onChange({ ...section, data: { ...d, [key]: value } });

  switch (section.type) {
    case 'heading':
      return (
        <div className="space-y-3">
          <Field label="Eyebrow" value={d.eyebrow || ''} onChange={(e) => set('eyebrow', e.target.value)} />
          <Field label="Judul" value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
          <TextArea label="Deskripsi" value={d.description || ''} onChange={(e) => set('description', e.target.value)} />
        </div>
      );
    case 'richtext':
      return <TextArea label="Konten" rows={8} value={d.content || ''} onChange={(e) => set('content', e.target.value)} />;
    case 'image':
      return (
        <div className="space-y-3">
          <ImageUploadField label="Gambar" value={d.src || ''} onChange={(v) => set('src', v)} />
          <Field label="Alt Text" value={d.alt || ''} onChange={(e) => set('alt', e.target.value)} />
          <Field label="Caption" value={d.caption || ''} onChange={(e) => set('caption', e.target.value)} />
        </div>
      );
    case 'image_text':
      return (
        <div className="space-y-3">
          <Field label="Eyebrow" value={d.eyebrow || ''} onChange={(e) => set('eyebrow', e.target.value)} />
          <Field label="Judul" value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
          <TextArea label="Teks" rows={5} value={d.body || ''} onChange={(e) => set('body', e.target.value)} />
          <ImageUploadField label="Gambar" value={d.image || ''} onChange={(v) => set('image', v)} />
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Posisi Gambar</label>
            <select value={d.imagePosition || 'left'} onChange={(e) => set('imagePosition', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
              <option value="left">Kiri</option>
              <option value="right">Kanan</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Badge Angka" value={d.badgeValue || ''} onChange={(e) => set('badgeValue', e.target.value)} />
            <Field label="Badge Label" value={d.badgeLabel || ''} onChange={(e) => set('badgeLabel', e.target.value)} />
          </div>
        </div>
      );
    case 'features':
      return (
        <div className="space-y-4">
          {(d.items || []).map((item, idx) => (
            <div key={idx} className="p-3 rounded-xl border border-gray-100 bg-gray-50 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-gray-500">Kartu {idx + 1}</span>
                <button
                  type="button"
                  className="text-xs text-red-600"
                  onClick={() => {
                    const items = [...(d.items || [])];
                    items.splice(idx, 1);
                    set('items', items);
                  }}
                >
                  Hapus
                </button>
              </div>
              <Field
                label="Judul"
                value={item.title || ''}
                onChange={(e) => {
                  const items = [...(d.items || [])];
                  items[idx] = { ...items[idx], title: e.target.value };
                  set('items', items);
                }}
              />
              <TextArea
                label="Poin (satu baris = satu poin)"
                rows={4}
                value={typeof item.items === 'string' ? item.items : (item.items || []).join('\n')}
                onChange={(e) => {
                  const items = [...(d.items || [])];
                  items[idx] = { ...items[idx], items: e.target.value };
                  set('items', items);
                }}
              />
            </div>
          ))}
          <button
            type="button"
            className="text-sm font-semibold text-japo-green"
            onClick={() => set('items', [...(d.items || []), { title: 'Fitur Baru', items: 'Poin 1\nPoin 2' }])}
          >
            + Tambah Kartu
          </button>
        </div>
      );
    case 'stats':
      return (
        <div className="space-y-3">
          {(d.items || []).map((item, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-2">
              <Field
                label="Nilai"
                value={item.value || ''}
                onChange={(e) => {
                  const items = [...(d.items || [])];
                  items[idx] = { ...items[idx], value: e.target.value };
                  set('items', items);
                }}
              />
              <Field
                label="Label"
                value={item.label || ''}
                onChange={(e) => {
                  const items = [...(d.items || [])];
                  items[idx] = { ...items[idx], label: e.target.value };
                  set('items', items);
                }}
              />
            </div>
          ))}
        </div>
      );
    case 'cta':
      return (
        <div className="space-y-3">
          <Field label="Judul" value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
          <TextArea label="Deskripsi" value={d.description || ''} onChange={(e) => set('description', e.target.value)} />
          <Field label="Teks Tombol" value={d.buttonText || ''} onChange={(e) => set('buttonText', e.target.value)} />
          <Field label="URL Tombol (kosong = WhatsApp)" value={d.buttonUrl || ''} onChange={(e) => set('buttonUrl', e.target.value)} />
        </div>
      );
    case 'html':
      return <TextArea label="HTML" rows={10} value={d.html || ''} onChange={(e) => set('html', e.target.value)} />;
    case 'products':
    case 'articles':
    case 'branches':
      return (
        <div className="space-y-3">
          <Field label="Eyebrow" value={d.eyebrow || ''} onChange={(e) => set('eyebrow', e.target.value)} />
          <Field label="Judul" value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
          <TextArea label="Deskripsi" value={d.description || ''} onChange={(e) => set('description', e.target.value)} />
          <Field label="Limit" type="number" value={d.limit || 3} onChange={(e) => set('limit', Number(e.target.value))} />
        </div>
      );
    case 'marketplace':
      return (
        <div className="space-y-3">
          <Field label="Eyebrow" value={d.eyebrow || ''} onChange={(e) => set('eyebrow', e.target.value)} />
          <Field label="Judul" value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
          <TextArea label="Deskripsi" value={d.description || ''} onChange={(e) => set('description', e.target.value)} />
          <ImageUploadField label="Gambar Samping" value={d.image || ''} onChange={(v) => set('image', v)} />
        </div>
      );
    default:
      return <p className="text-sm text-gray-500">Tidak ada field untuk tipe ini.</p>;
  }
}

export default function PageBuilderAdmin({ pageBuilder, setPageBuilder, toast, refreshGlobalSettings }) {
  const resolved = useMemo(() => resolvePageBuilder(pageBuilder), [pageBuilder]);
  const pagesList = useMemo(
    () => Object.values(resolved.pages).sort((a, b) => {
      if (a.isSystem && !b.isSystem) return -1;
      if (!a.isSystem && b.isSystem) return 1;
      return (a.name || '').localeCompare(b.name || '');
    }),
    [resolved]
  );

  const [editPage, setEditPage] = useState(null);
  const [editSectionIdx, setEditSectionIdx] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newPage, setNewPage] = useState({ name: '', slug: '' });
  const [tab, setTab] = useState('content');
  const [saving, setSaving] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);

  const persist = async (nextBuilder) => {
    setSaving(true);
    try {
      if (!localStorage.getItem('belbel_token')) {
        toast({
          title: 'Sesi habis',
          description: 'Silakan login ulang sebelum menyimpan Page Builder.',
          variant: 'destructive',
        });
        return false;
      }

      // Ambil settings terbaru agar footer/kategori/pages lain tidak tertimpa
      const latest = await fetchSettings().catch(() => ({}));
      const base = latest?.page_builder && typeof latest.page_builder === 'object'
        ? latest.page_builder
        : (pageBuilder || {});
      const merged = {
        ...base,
        ...(nextBuilder || {}),
        pages: {
          ...(base.pages || {}),
          ...((nextBuilder && nextBuilder.pages) || {}),
        },
      };
      if (nextBuilder?.footer) merged.footer = nextBuilder.footer;
      if (nextBuilder?.product_categories) merged.product_categories = nextBuilder.product_categories;
      if (nextBuilder?.home) merged.home = nextBuilder.home;

      await adminUpdateSettings({ page_builder: merged });
      setPageBuilder(merged);
      toast({ title: 'Page Builder tersimpan' });
      await refreshGlobalSettings();
      return true;
    } catch (err) {
      toast({ title: 'Gagal menyimpan', description: apiErrorMessage(err), variant: 'destructive' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (slug) => {
    const page = resolved.pages[slug];
    if (!page) return;
    setEditPage(JSON.parse(JSON.stringify(page)));
    setEditSectionIdx(null);
    setTab(page.template === 'blank' ? 'sections' : 'content');
  };

  const saveEditPage = async () => {
    if (!editPage) return;
    const next = {
      ...(pageBuilder || {}),
      pages: { ...(pageBuilder?.pages || {}) },
    };
    next.pages[editPage.slug] = {
      name: editPage.name,
      slug: editPage.slug,
      status: editPage.status || 'published',
      template: editPage.template,
      isSystem: !!editPage.isSystem,
      title: editPage.title,
      subtitle: editPage.subtitle,
      content: editPage.content || {},
      sections: editPage.sections || [],
    };
    // keep legacy home keys in sync
    if (editPage.slug === '/') {
      next.home = {
        heroTitle: editPage.content?.heroTitle || '',
        heroSubtitle: editPage.content?.heroSubtitle || '',
      };
    }
    const ok = await persist(next);
    if (ok) setEditPage(null);
  };

  const createPage = async () => {
    const name = newPage.name.trim() || 'Halaman Baru';
    let slug = (newPage.slug.trim() || slugifyPath(name));
    if (!slug.startsWith('/')) slug = `/${slug}`;
    if (resolved.pages[slug]) {
      toast({ title: 'Slug sudah dipakai', description: slug, variant: 'destructive' });
      return;
    }
    const page = createBlankPage(name, slug);
    const next = {
      ...(pageBuilder || {}),
      pages: { ...(pageBuilder?.pages || {}), [slug]: page },
    };
    const ok = await persist(next);
    if (ok) {
      setAddOpen(false);
      setNewPage({ name: '', slug: '' });
      openEdit(slug);
    }
  };

  const deletePage = async (slug) => {
    const page = resolved.pages[slug];
    if (!page || page.isSystem) return;
    if (!window.confirm(`Hapus halaman "${page.name}"?`)) return;
    const next = {
      ...(pageBuilder || {}),
      pages: { ...(pageBuilder?.pages || {}) },
    };
    delete next.pages[slug];
    await persist(next);
  };

  const moveSection = (idx, dir) => {
    setEditPage((p) => {
      const sections = [...(p.sections || [])];
      const j = idx + dir;
      if (j < 0 || j >= sections.length) return p;
      [sections[idx], sections[j]] = [sections[j], sections[idx]];
      return { ...p, sections };
    });
  };

  const contentFields = TEMPLATE_CONTENT_FIELDS[editPage?.template] || [];

  return (
    <div className="bg-white rounded-2xl p-8 border border-gray-100">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-bold text-lg text-gray-900">Page Builder</h3>
          <p className="text-sm text-gray-500 mt-1">
            Edit semua laman default, tambah section dinamis, dan buat halaman baru seperti WordPress.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm('Reset semua Page Builder ke default?')) return;
              setSaving(true);
              try {
                await adminUpdateSettings({ page_builder: null });
                setPageBuilder({});
                toast({ title: 'Page Builder direset ke default' });
                await refreshGlobalSettings();
              } catch (err) {
                toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
              } finally {
                setSaving(false);
              }
            }}
            className="text-xs font-semibold text-red-600 hover:text-red-700"
          >
            Reset Default
          </button>
          <button
            type="button"
            onClick={() => { setAddOpen(true); setNewPage({ name: '', slug: '' }); }}
            className="inline-flex items-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            <Plus className="w-4 h-4" /> Tambah Halaman
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {pagesList.map((p) => (
          <div key={p.slug} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-japo-green/40 hover:bg-rose-50/30">
            <div className="min-w-0">
              <div className="font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                {p.name}
                {p.isSystem ? (
                  <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Default</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-japo-yellow/20 text-gray-800">Custom</span>
                )}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                <span>{p.slug}</span>
                <span>·</span>
                <span>{(p.sections || []).length} section ekstra</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${p.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                {p.status === 'draft' ? 'Draft' : 'Published'}
              </span>
              <a href={p.slug} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-100 rounded-lg" title="Lihat">
                <ExternalLink className="w-4 h-4 text-gray-600" />
              </a>
              <button type="button" onClick={() => openEdit(p.slug)} className="p-2 hover:bg-gray-100 rounded-lg" title="Edit">
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
              {!p.isSystem && (
                <button type="button" onClick={() => deletePage(p.slug)} className="p-2 hover:bg-red-50 rounded-lg" title="Hapus">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create page */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Tambah Halaman Baru" size="md">
        <div className="space-y-4">
          <Field
            label="Nama Halaman"
            value={newPage.name}
            onChange={(e) => setNewPage({ name: e.target.value, slug: slugifyPath(e.target.value) })}
            placeholder="Promo Ramadan"
          />
          <Field
            label="URL / Slug"
            value={newPage.slug}
            onChange={(e) => setNewPage((n) => ({ ...n, slug: e.target.value }))}
            placeholder="/promo-ramadan"
          />
          <p className="text-xs text-gray-500">Halaman custom memakai template kosong + section yang bisa ditambah dinamis.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAddOpen(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold">Batal</button>
            <button type="button" disabled={saving} onClick={createPage} className="px-4 py-2 rounded-lg bg-japo-green text-white text-sm font-semibold">Buat Halaman</button>
          </div>
        </div>
      </Modal>

      {/* Edit page */}
      <Modal open={!!editPage} onClose={() => setEditPage(null)} title={`Edit: ${editPage?.name || ''}`} size="full">
        {editPage && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nama" value={editPage.name} onChange={(e) => setEditPage({ ...editPage, name: e.target.value })} />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
                <select
                  value={editPage.status || 'published'}
                  onChange={(e) => setEditPage({ ...editPage, status: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <Field label="Banner / Judul Page" value={editPage.title || ''} onChange={(e) => setEditPage({ ...editPage, title: e.target.value })} />
              <Field
                label="Slug (URL)"
                value={editPage.slug}
                disabled={editPage.isSystem}
                onChange={(e) => !editPage.isSystem && setEditPage({ ...editPage, slug: e.target.value.startsWith('/') ? e.target.value : `/${e.target.value}` })}
              />
            </div>
            <TextArea label="Banner Subtitle" rows={2} value={editPage.subtitle || ''} onChange={(e) => setEditPage({ ...editPage, subtitle: e.target.value })} />

            <div className="flex gap-2 border-b border-gray-100 pb-2">
              {editPage.template !== 'blank' && (
                <button type="button" onClick={() => setTab('content')} className={`px-4 py-2 text-sm font-semibold rounded-lg ${tab === 'content' ? 'bg-japo-green text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  Konten Template
                </button>
              )}
              <button type="button" onClick={() => setTab('sections')} className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-2 ${tab === 'sections' ? 'bg-japo-green text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Layers className="w-4 h-4" /> Section Dinamis ({(editPage.sections || []).length})
              </button>
            </div>

            {tab === 'content' && editPage.template !== 'blank' && (
              <div className="space-y-4 max-h-[48vh] overflow-y-auto pr-1">
                {contentFields.length === 0 ? (
                  <p className="text-sm text-gray-500">Template ini memakai data katalog. Edit banner di atas, atau tambah section dinamis di tab sebelah.</p>
                ) : (
                  contentFields.map((f) => {
                    const setContent = (value) => setEditPage({
                      ...editPage,
                      content: { ...(editPage.content || {}), [f.key]: value },
                    });
                    if (f.type === 'image') {
                      return (
                        <ImageUploadField
                          key={f.key}
                          label={f.label}
                          value={editPage.content?.[f.key] || ''}
                          onChange={setContent}
                        />
                      );
                    }
                    if (f.multiline) {
                      return (
                        <TextArea
                          key={f.key}
                          label={f.label}
                          rows={3}
                          value={editPage.content?.[f.key] || ''}
                          onChange={(e) => setContent(e.target.value)}
                        />
                      );
                    }
                    return (
                      <Field
                        key={f.key}
                        label={f.label}
                        value={editPage.content?.[f.key] || ''}
                        onChange={(e) => setContent(e.target.value)}
                      />
                    );
                  })
                )}
              </div>
            )}

            {tab === 'sections' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Section ditampilkan di bawah konten template default.</p>
                  <button type="button" onClick={() => setAddSectionOpen(true)} className="inline-flex items-center gap-1 text-sm font-semibold text-japo-green">
                    <Plus className="w-4 h-4" /> Tambah Section
                  </button>
                </div>
                {(editPage.sections || []).length === 0 && (
                  <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl text-sm text-gray-500">
                    Belum ada section. Tambahkan heading, teks, CTA, produk, dll.
                  </div>
                )}
                {(editPage.sections || []).map((sec, idx) => (
                  <div key={sec.id} className="flex items-center gap-2 p-3 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900 capitalize">{SECTION_TYPES.find((t) => t.type === sec.type)?.label || sec.type}</div>
                      <div className="text-xs text-gray-500 truncate">{sec.data?.title || sec.data?.eyebrow || sec.type}</div>
                    </div>
                    <button type="button" className="p-1.5 hover:bg-white rounded" onClick={() => moveSection(idx, -1)}><ChevronUp className="w-4 h-4" /></button>
                    <button type="button" className="p-1.5 hover:bg-white rounded" onClick={() => moveSection(idx, 1)}><ChevronDown className="w-4 h-4" /></button>
                    <button
                      type="button"
                      className="p-1.5 hover:bg-white rounded"
                      onClick={() => setEditPage({
                        ...editPage,
                        sections: editPage.sections.map((s, i) => i === idx ? { ...s, visible: s.visible === false } : s),
                      })}
                    >
                      {sec.visible === false ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-japo-green" />}
                    </button>
                    <button type="button" className="p-1.5 hover:bg-white rounded" onClick={() => setEditSectionIdx(idx)}><Edit className="w-4 h-4" /></button>
                    <button
                      type="button"
                      className="p-1.5 hover:bg-red-50 rounded"
                      onClick={() => setEditPage({ ...editPage, sections: editPage.sections.filter((_, i) => i !== idx) })}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setEditPage(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold">Batal</button>
              <button type="button" disabled={saving} onClick={saveEditPage} className="px-4 py-2 rounded-lg bg-japo-green text-white text-sm font-semibold">
                {saving ? 'Menyimpan...' : 'Simpan Halaman'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add section type picker */}
      <Modal open={addSectionOpen} onClose={() => setAddSectionOpen(false)} title="Pilih Tipe Section" size="lg">
        <div className="grid sm:grid-cols-2 gap-3">
          {SECTION_TYPES.map((t) => (
            <button
              key={t.type}
              type="button"
              className="text-left p-4 rounded-xl border border-gray-100 hover:border-japo-green hover:bg-rose-50/40 transition-colors"
              onClick={() => {
                setEditPage((p) => ({ ...p, sections: [...(p.sections || []), createSection(t.type)] }));
                setAddSectionOpen(false);
                setTab('sections');
              }}
            >
              <div className="font-bold text-sm text-gray-900">{t.label}</div>
              <div className="text-xs text-gray-500 mt-1">{t.desc}</div>
            </button>
          ))}
        </div>
      </Modal>

      {/* Edit one section */}
      <Modal
        open={editSectionIdx !== null}
        onClose={() => setEditSectionIdx(null)}
        title={`Edit Section: ${editPage?.sections?.[editSectionIdx]?.type || ''}`}
        size="lg"
      >
        {editPage && editSectionIdx !== null && editPage.sections[editSectionIdx] && (
          <div className="space-y-4">
            <SectionDataFields
              section={editPage.sections[editSectionIdx]}
              onChange={(nextSec) => {
                const sections = [...editPage.sections];
                sections[editSectionIdx] = nextSec;
                setEditPage({ ...editPage, sections });
              }}
            />
            <div className="flex justify-end">
              <button type="button" onClick={() => setEditSectionIdx(null)} className="px-4 py-2 rounded-lg bg-japo-green text-white text-sm font-semibold">
                Selesai
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
